import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator,
  List,
  TrendingUp,
  FileText,
  ChevronLeft,
  AlertTriangle,
  Gauge,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { projectAnalysisApi } from '../lib/api/projectAnalysis';
import LoadingSpinner from './common/LoadingSpinner';
import { formatMoney } from '../lib/numberFormat';

const formatPrice = (n: string | number | null | undefined): string => {
  const num = typeof n === 'string' ? parseFloat(String(n).replace(/[,٬\s]/g, '')) : n;
  if (num == null || Number.isNaN(num) || num === 0) return '—';
  if (Math.abs(num) >= 1e12) return (num / 1e12).toFixed(1) + ' همت';
  if (Math.abs(num) >= 1e9) return (num / 1e9).toFixed(1) + ' میلیارد';
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + ' میلیون';
  return formatMoney(num);
};

interface DashboardStats {
  total: number;
  active_count: number;
  recent_count: number;
  loss_count?: number;
  portfolio_value?: number;
  portfolio_base?: number;
  portfolio_profit?: number;
  avg_coefficient?: number | null;
  coefficient_count?: number;
  recent_analyses?: Array<{
    id: number;
    name: string;
    project_id: number;
    total_cost: string | null;
    total_base_cost?: string | null;
    total_profit?: string | null;
    initial_estimate?: string | null;
    created_at: string;
    is_active: boolean;
    sections_count: number;
    project?: { id: number; name: string; contract_number?: string; initial_estimate?: string | null };
  }>;
}

const coefficientOf = (a: NonNullable<DashboardStats['recent_analyses']>[number]): number | null => {
  const total = parseFloat(String(a.total_cost ?? '').replace(/[,٬\s]/g, '')) || 0;
  const est = parseFloat(String(a.initial_estimate ?? a.project?.initial_estimate ?? '').replace(/[,٬\s]/g, '')) || 0;
  if (total <= 0 || est <= 0) return null;
  return total / est;
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    projectAnalysisApi
      .dashboardStats()
      .then((res) => setStats(res.data as DashboardStats))
      .catch(() => setStats({ total: 0, active_count: 0, recent_count: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const recentChart = useMemo(() => {
    return (stats?.recent_analyses ?? [])
      .slice(0, 8)
      .map((a) => ({
        name: a.name.length > 18 ? `${a.name.slice(0, 18)}…` : a.name,
        value: parseFloat(String(a.total_cost ?? '').replace(/[,٬\s]/g, '')) || 0,
      }))
      .filter((d) => d.value > 0);
  }, [stats]);

  if (loading) {
    return <LoadingSpinner variant="page" />;
  }

  const s = stats ?? { total: 0, active_count: 0, recent_count: 0 };
  const inactiveCount = Math.max(0, s.total - s.active_count);
  const chartData = [
    { name: 'فعال', value: s.active_count, color: '#0a4d29' },
    { name: 'غیرفعال', value: inactiveCount, color: '#94a3b8' },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-5 pb-6" dir="rtl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">داشبورد آنالیز بها</h1>
          <p className="text-sm text-slate-500 mt-1">نمای کلی پرتفوی، ضریب پیمانکار و آنالیزهای اخیر</p>
        </div>
        <Link
          to="/analyses/create"
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 bg-[#0a4d29] text-white rounded-lg text-sm font-bold hover:bg-emerald-900"
        >
          <Calculator size={16} />
          آنالیز جدید
        </Link>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-6 gap-3">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[11px] text-slate-500 font-bold">تعداد آنالیزها</p>
          <p className="text-2xl font-black text-slate-900 mt-1">{s.total}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[11px] text-slate-500 font-bold">فعال</p>
          <p className="text-2xl font-black text-emerald-800 mt-1">{s.active_count}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[11px] text-slate-500 font-bold">۳۰ روز اخیر</p>
          <p className="text-2xl font-black text-slate-900 mt-1">{s.recent_count}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[11px] text-slate-500 font-bold">ارزش پرتفوی</p>
          <p className="text-xl font-black text-slate-900 mt-1">{formatPrice(s.portfolio_value)}</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-[11px] text-slate-500 font-bold">میانگین ضریب</p>
          <p className="text-2xl font-black text-teal-800 mt-1" dir="ltr">
            {s.avg_coefficient != null ? Number(s.avg_coefficient).toFixed(4) : '—'}
          </p>
        </div>
        <div className={`p-4 rounded-xl border shadow-sm ${ (s.loss_count ?? 0) > 0 ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
          <p className="text-[11px] text-slate-500 font-bold">آنالیز زیان‌ده</p>
          <p className={`text-2xl font-black mt-1 ${(s.loss_count ?? 0) > 0 ? 'text-rose-700' : 'text-slate-900'}`}>
            {s.loss_count ?? 0}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-500 mb-3">وضعیت آنالیزها</p>
          {chartData.length > 0 ? (
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} innerRadius={50} outerRadius={74} paddingAngle={3} dataKey="value">
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-52 flex items-center justify-center text-slate-400 text-sm">داده‌ای نیست</div>
          )}
          <div className="flex justify-center gap-4 text-xs text-slate-600">
            <span>فعال {s.active_count}</span>
            <span>غیرفعال {inactiveCount}</span>
          </div>
        </div>

        <div className="xl:col-span-2 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <p className="text-xs font-bold text-slate-500 mb-3">ارزش آنالیزهای اخیر</p>
          {recentChart.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recentChart}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                  <YAxis hide />
                  <Tooltip formatter={(v: number) => formatMoney(v)} />
                  <Bar dataKey="value" fill="#0a4d29" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-56 flex items-center justify-center text-slate-400 text-sm">هنوز آنالیزی نیست</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
              <TrendingUp className="text-emerald-800" size={22} />
            </div>
            <div>
              <h4 className="font-bold text-slate-800">کار سریع</h4>
              <p className="text-xs text-slate-500">متره، آنالیز و پیشنهاد قیمت</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/analyses" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0a4d29] text-white rounded-lg text-sm font-bold">
              <List size={16} /> لیست آنالیزها
            </Link>
            <Link to="/analyses/create" className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-800 rounded-lg text-sm font-bold hover:bg-slate-50">
              <Calculator size={16} /> آنالیز جدید
            </Link>
            <Link to="/bid-domain" className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-300 text-slate-800 rounded-lg text-sm font-bold hover:bg-slate-50">
              <Gauge size={16} /> تعیین دامنه
            </Link>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[11px] text-slate-500 font-bold">جمع پایه</p>
              <p className="font-mono font-bold mt-1">{formatPrice(s.portfolio_base)}</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-3">
              <p className="text-[11px] text-slate-500 font-bold">جمع سود</p>
              <p className="font-mono font-bold mt-1">{formatPrice(s.portfolio_profit)}</p>
            </div>
            <div className="rounded-lg bg-teal-50 p-3">
              <p className="text-[11px] text-slate-500 font-bold">نمونه ضریب</p>
              <p className="font-mono font-bold mt-1">{s.coefficient_count ?? 0} آنالیز</p>
            </div>
          </div>
          {(s.loss_count ?? 0) > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
              <AlertTriangle size={16} />
              {s.loss_count} آنالیز سود منفی دارد و باید بازنگری شود.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-800">آخرین آنالیزها</h3>
          <Link to="/analyses" className="inline-flex items-center gap-1 text-sm text-emerald-800 font-bold hover:underline">
            مشاهده همه
            <ChevronLeft size={16} />
          </Link>
        </div>
        {s.recent_analyses && s.recent_analyses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-right text-slate-500 text-xs bg-slate-50">
                  <th className="p-3">نام آنالیز</th>
                  <th className="p-3">پروژه</th>
                  <th className="p-3">بخش‌ها</th>
                  <th className="p-3">بهای کل</th>
                  <th className="p-3">سود</th>
                  <th className="p-3">ضریب</th>
                  <th className="p-3">وضعیت</th>
                  <th className="p-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {s.recent_analyses.map((a) => {
                  const coef = coefficientOf(a);
                  const profit = parseFloat(String(a.total_profit ?? '').replace(/[,٬\s]/g, '')) || 0;
                  return (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-bold text-slate-800">{a.name}</td>
                      <td className="p-3">{a.project?.name ?? '—'}</td>
                      <td className="p-3 font-mono">{a.sections_count ?? 0}</td>
                      <td className="p-3 font-mono">{formatPrice(a.total_cost)}</td>
                      <td className={`p-3 font-mono ${profit < 0 ? 'text-rose-700 font-bold' : ''}`}>{formatPrice(a.total_profit)}</td>
                      <td className="p-3 font-mono" dir="ltr">{coef != null ? coef.toFixed(4) : '—'}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${a.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                          {a.is_active ? 'فعال' : 'غیرفعال'}
                        </span>
                      </td>
                      <td className="p-3">
                        <Link to={`/analyses/${a.id}`} className="text-emerald-800 hover:underline text-xs font-bold">
                          باز کردن
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center text-slate-500">
            <FileText size={40} className="mx-auto mb-2 opacity-50" />
            <p>آنالیزی ثبت نشده است.</p>
            <Link to="/analyses/create" className="text-emerald-800 font-bold hover:underline mt-2 inline-block">
              ایجاد آنالیز جدید
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
